import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UploadSimple, Trash, ImageSquare, Tag, LinkSimple, TreeStructure } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import { Input, Textarea, Select, Checkbox, Button, SeoFields } from "../../components/ui";

const MAX_DEPTH = 3; // 3 levels => a parent may be at depth 0 or 1

/* Flatten the nested category tree into [{ id, name, depth }]. */
const flattenTree = (nodes, depth = 0, acc = []) => {
  (nodes || []).forEach((n) => {
    acc.push({ id: n.id, name: n.name, depth });
    if (n.children?.length) flattenTree(n.children, depth + 1, acc);
  });
  return acc;
};

/* Collect the id of a node and all of its descendants (to prevent cycles). */
const collectSubtreeIds = (nodes, targetId, acc = new Set()) => {
  (nodes || []).forEach((n) => {
    if (n.id === targetId) {
      const walk = (node) => {
        acc.add(node.id);
        (node.children || []).forEach(walk);
      };
      walk(n);
    } else if (n.children?.length) {
      collectSubtreeIds(n.children, targetId, acc);
    }
  });
  return acc;
};

const emptyForm = { image: null, image_alt: "", name: "", description: "", slug: "", parent_id: "", featured: false, meta_title: "", meta_description: "" };

const CategoryModal = ({
  isOpen,
  onClose,
  setCategoryToUpdate,
  CategoryToUpdate,
  categories,
  presetParentId,
  setDone,
  done,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => (document.body.style.overflow = "unset");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && CategoryToUpdate?.id) {
      setFormData({
        image: CategoryToUpdate.image || null,
        image_alt: CategoryToUpdate.image_alt || "",
        name: CategoryToUpdate.name || "",
        description: CategoryToUpdate.description || "",
        slug: CategoryToUpdate.slug || "",
        parent_id: CategoryToUpdate.parent_id || "",
        featured: CategoryToUpdate.featured || false,
        meta_title: CategoryToUpdate.meta_title || "",
        meta_description: CategoryToUpdate.meta_description || "",
      });
    } else {
      setFormData({ ...emptyForm, parent_id: presetParentId || "" });
    }
  }, [isOpen, CategoryToUpdate, presetParentId]);

  const parentOptions = useMemo(() => {
    const excluded = CategoryToUpdate?.id ? collectSubtreeIds(categories, CategoryToUpdate.id) : new Set();
    return flattenTree(categories)
      .filter((o) => o.depth < MAX_DEPTH - 1 && !excluded.has(o.id))
      .map((o) => ({ value: o.id, label: `${"— ".repeat(o.depth)}${o.name}` }));
  }, [categories, CategoryToUpdate]);

  const isRoot = !formData.parent_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        image: formData.image,
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        parent_id: formData.parent_id || null,
        featured: isRoot ? !!formData.featured : false,
        meta_title: formData.meta_title,
        meta_description: formData.meta_description,
        image_alt: formData.image_alt,
      };
      const response = CategoryToUpdate?.id
        ? await axiosInstance.put(`/categories/update/${CategoryToUpdate.id}`, submitData)
        : await axiosInstance.post("/categories/create", submitData);
      if (response.data) {
        toast.success(response.data.message);
        CloseModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.warning("File size exceeds 15 MB limit");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setFormData((prev) => ({ ...prev, image: reader.result }));
  };

  const CloseModal = () => {
    setFormData(emptyForm);
    setCategoryToUpdate(null);
    setDone(!done);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="category-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={CloseModal}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-pop"
            onClick={(e) => e.stopPropagation()}
            aria-modal="true"
            role="dialog"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">
                  {CategoryToUpdate?.id ? "Edit category" : "New category"}
                </h2>
                <p className="text-xs text-muted">Up to {MAX_DEPTH} nesting levels.</p>
              </div>
              <button type="button" onClick={CloseModal} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <form id="category-form" onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
              {/* Image uploader */}
              <div>
                <label className="mb-2 block text-sm font-medium text-ink/90">Image</label>
                <div className="flex items-center gap-4">
                  <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl border border-line bg-surface-2">
                    {formData.image ? (
                      <img src={formData.image} alt="Category" className="h-full w-full object-cover" />
                    ) : (
                      <ImageSquare size={24} className="text-faint" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink transition hover:bg-surface-2">
                        <UploadSimple size={16} /> Upload
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                      </label>
                      {formData.image && (
                        <button
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, image: null }))}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-red-600 transition hover:bg-red-500/10"
                        >
                          <Trash size={16} /> Remove
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted">JPG, PNG or GIF · max 15MB</p>
                  </div>
                </div>
                {formData.image && (
                  <Input
                    containerClassName="mt-3"
                    label="Image alt text"
                    value={formData.image_alt}
                    onChange={(e) => setFormData((p) => ({ ...p, image_alt: e.target.value }))}
                    placeholder="Describe the image for SEO & accessibility"
                  />
                )}
              </div>

              <Select
                label="Parent category"
                icon={TreeStructure}
                placeholder="Top-level category (none)"
                clearable
                searchable
                options={parentOptions}
                value={formData.parent_id || null}
                onChange={(v) => setFormData((p) => ({ ...p, parent_id: v || "" }))}
              />

              <Input label="Name" icon={Tag} name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Power Tools" required />
              <Input label="Slug" icon={LinkSimple} name="slug" value={formData.slug} onChange={handleChange} placeholder="e.g. power-tools" required />
              <Textarea label="Description" name="description" value={formData.description} onChange={handleChange} placeholder="Short description of the category…" />

              {isRoot && (
                <Checkbox
                  checked={!!formData.featured}
                  onChange={(val) => setFormData((p) => ({ ...p, featured: val }))}
                  label="Feature this category on the homepage"
                  description="When checked, this category appears on the homepage for better visibility."
                />
              )}

              <SeoFields
                value={{ slug: formData.slug, meta_title: formData.meta_title, meta_description: formData.meta_description }}
                onChange={(field, val) => setFormData((p) => ({ ...p, [field]: val }))}
                fallbackTitle={formData.name}
                fallbackDescription={formData.description}
                basePath="/category/"
                showSlug={false}
              />
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={CloseModal}>
                Cancel
              </Button>
              <Button type="submit" form="category-form" loading={loading}>
                {CategoryToUpdate?.id ? "Save changes" : "Create category"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CategoryModal;
